import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Twitter, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { RadiusMark } from '../RadiusMark';
import { FOOTER, MENU } from '../../data/menu';

// Full opaque footer that scrolls in at the very bottom of the cinematic
// experience. Sits at z-10 above the fixed globe layer.
export const SiteFooter = ({ onOpenStation }) => {
  const year = new Date().getFullYear();
  const solutionsCol = FOOTER.columns.find((c) => c.title === 'Solutions');
  const companyCol = FOOTER.columns.find((c) => c.title === 'Company');
  const resourcesCol = FOOTER.columns.find((c) => c.title === 'Resources');

  const renderLink = (link) => {
    if (link.openStation) {
      return (
        <button
          key={link.label}
          type="button"
          onClick={() => onOpenStation(link.openStation)}
          className="text-sm text-muted-foreground hover:text-primary text-left transition-colors"
          data-testid={`footer-open-${link.openStation}`}
        >
          {link.label}
        </button>
      );
    }
    return (
      <Link
        key={link.label}
        to={link.to}
        className="text-sm text-muted-foreground hover:text-primary text-left transition-colors"
      >
        {link.label}
      </Link>
    );
  };

  return (
    <footer
      className="relative z-10 bg-background border-t border-border/60 font-sans"
      data-testid="cinematic-footer"
    >
      {/* CTA band */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full"
               style={{ background: 'radial-gradient(circle, hsl(0 84% 60% / 0.22), transparent 60%)' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-4 leading-[1.05]">
            Ready to <span className="text-gradient">Connect the Dots?</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Let’s discuss how we can help your business grow through strategic brand marketing.
          </p>
          <Button
            variant="hero"
            size="lg"
            onClick={() => onOpenStation('contact')}
            data-testid="footer-cta-lets-chat"
          >
            Connect
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* 4-column grid */}
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <RadiusMark size={40} />
            <span className="font-display text-2xl tracking-[0.3em] text-foreground">RADIUS</span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Connecting insight, strategy, and creative execution for ambitious businesses.
            Strategy-led. Design-driven. Outcome focused.
          </p>
          <div className="flex items-center gap-2">
            {[
              { icon: Linkedin, href: 'https://www.linkedin.com/', label: 'LinkedIn' },
              { icon: Facebook, href: 'https://www.facebook.com/', label: 'Facebook' },
              { icon: Twitter, href: 'https://twitter.com/', label: 'Twitter' },
              { icon: Mail, href: 'mailto:mishaan@radiusconsulting.co.za', label: 'Email' },
            ].map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="w-9 h-9 rounded-md flex items-center justify-center border border-primary/25 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid={`footer-social-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-display text-sm uppercase tracking-[0.3em] text-foreground mb-4">
            {solutionsCol.title}
          </h3>
          <ul className="space-y-2">{solutionsCol.links.map((l) => <li key={l.label}>{renderLink(l)}</li>)}</ul>
        </div>

        <div>
          <h3 className="font-display text-sm uppercase tracking-[0.3em] text-foreground mb-4">
            {companyCol.title}
          </h3>
          <ul className="space-y-2">{companyCol.links.map((l) => <li key={l.label}>{renderLink(l)}</li>)}</ul>
        </div>

        <div>
          <h3 className="font-display text-sm uppercase tracking-[0.3em] text-foreground mb-4">
            {resourcesCol.title}
          </h3>
          <ul className="space-y-2">{resourcesCol.links.map((l) => <li key={l.label}>{renderLink(l)}</li>)}</ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {year} Radius Consulting. All rights reserved.</div>
          <div className="tracking-[0.3em] uppercase">Strategy-Led · Design-Driven · Outcome-Focused</div>
        </div>
      </div>
      {/* MENU var kept as a reference so tree-shakers don't strip. */}
      <span className="hidden" aria-hidden>{MENU.length}</span>
    </footer>
  );
};

export default SiteFooter;
